import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles/custom-table.css';
import ICustomTableProps from './interfaces/i-custom-table-props';
import { formatDate, formatValueIfNullOrUndefined } from 'utilities/util';
import { status_text } from 'components/exception-management/constants';
import Tooltip from 'components/tooltip';
import { ModalMain } from 'damco-components';
import {
    BULK_DOWNLOAD_FETCH_CONCURRENCY,
    BULK_DOWNLOAD_MAX_ROWS,
    collectRowsForBulkDownload
} from 'utilities/bulkDownloadCollection';

type BulkDownloadPhase = 'idle' | 'confirm' | 'preparing' | 'partial' | 'error';

const CustomTable = (props: ICustomTableProps) => {
    const {
        tableId,
        isBulkFeatureEnabled,
        isFeatureEnabled,
        hasUAMPermission,
        selectedPage,
        onImportChargesClick,
        columns,
        pageSize,
        rows,
        linkAction,
        pageCount,
        nextPageAction,
        previousPageAction,
        pageChangeAction,
        pageSizeChanged,
        downloadToFileAction,
        sortingAction,
        clearFilters,
        filtersApplied,
        totalCount,
        fetchRowsForBulkDownload
    } = props;

    const [localColumns, setLocalColumns] = useState(columns);

    const [count, setCount] = useState(1);

    const [sort, setSort] = useState<{ id: string | null; desc: boolean; }>({
        id: null,
        desc: false,
    });

    const [selectedRowData, setSelectedRowData] = useState<Array<{
        page: number;
        selectedRows: any[];
    }>>([]);

    const [showModal, setShowModal] = useState(false);

    const [bulkPhase, setBulkPhase] = useState<BulkDownloadPhase>('idle');
    const [bulkProgress, setBulkProgress] = useState({ fetched: 0, total: 0 });
    const [bulkPartial, setBulkPartial] = useState<{ rows: any[]; failedPages: number[] } | null>(null);
    const bulkCancelRef = useRef(false);
    const bulkGenerationRef = useRef(0);

    const getTooltp = (status: any) => {
        return `<span className="example-emoji" role="img" aria-label="duck emoji">
          ${status.substring(0, 12) + '...'}
        </span>`;
    };

    const formattedRows = useMemo(() => {
        if (!rows || rows.length === 0) {
            return [];
        }

        const getHmtl = (status: string) => <span dangerouslySetInnerHTML={{ __html: `${status_text.negative} ${status}` }} />;
        const getHmtlNew = (status: string) => <>
            <Tooltip content={status} direction="left">
                <span dangerouslySetInnerHTML={{ __html: `${getTooltp(status)}` }} />
            </Tooltip>
        </>;

        return rows.map((row: any) => {
            row.finOpsStatus = row.finOpsStatus && row.finOpsStatus.props ? row.finOpsStatus : row.rateErrorMessage ? getHmtl(row.finOpsStatus) : row.finOpsStatus;
            row.etd = formatDate(row.etd, 'YYYY-MM-DD');
            row.eta = formatDate(row.eta, 'YYYY-MM-DD');
            row.jobCreationDate = formatDate(row.jobCreationDate, 'YYYY-MM-DD');
            row.sealNumber1 = formatValueIfNullOrUndefined(row.sealNumber1);
            row.lastChangedBy = formatValueIfNullOrUndefined(row.lastChangedBy);
            row.createDate = formatDate(row.createDate, 'YYYY-MM-DD HH:mm:ss');
            row.updateDate = formatDate(row.updateDate, 'YYYY-MM-DD HH:mm:ss');
            row.lastChangeDate = formatDate(row.lastChangeDate, 'YYYY-MM-DD HH:mm:ss');
            if (tableId === 'jobCosting-search-exportJob') {
                row.exportJobStatus = row.exportJobStatus && row.exportJobStatus.props ? row.exportJobStatus : row.rateErrorMessage ? getHmtl(row.exportJobStatus) : row.exportJobStatus;
                row.estimatedTimeOfDeparture = formatDate(row.estimatedTimeOfDeparture, 'YYYY-MM-DD');
                row.estimatedTimeOfArrival = formatDate(row.estimatedTimeOfArrival, 'YYYY-MM-DD');
                row.rateErrorMessage = row.rateErrorMessage ? getHmtlNew(row.rateErrorMessage) : row.rateErrorMessage;
            }
            if (tableId === 'jobCosting-search-ets') {
                row.rateErrorMessage = row.rateErrorMessage ? getHmtlNew(row.rateErrorMessage) : row.rateErrorMessage;
            }
            return row;
        });
    }, [rows]);

    const selectedRowsCountForCurrentPage = useMemo(() => {
        const pageData = selectedRowData.find((item) => item.page === selectedPage);
        return pageData ? pageData.selectedRows.length : 0;
    }, [selectedRowData, selectedPage, filtersApplied, clearFilters]);

    const allChecked = useMemo(() => {
        return selectedRowsCountForCurrentPage === pageSize;
    }, [selectedRowsCountForCurrentPage, pageSize]);

    const noneChecked = useMemo(() => {
        return selectedRowsCountForCurrentPage === 0;
    }, [selectedRowsCountForCurrentPage]);

    const allSelectedRows = useMemo(() => {
        return selectedRowData.reduce((acc: any, item: any) => acc.concat(item.selectedRows), []);
    }, [selectedRowData]);

    const visibleColumns = useMemo(() => {
        return localColumns?.filter(col => col.show) || [];
    }, [localColumns]);

    const selectedRowIds = useMemo(() => {
        return new Set(allSelectedRows.map((r: any) => r.id));
    }, [allSelectedRows]);

    const resizeRefs = useRef<Array<HTMLTableCellElement | null>>([]);
    const headerCheckboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const savedColumns = localStorage.getItem(`${tableId}_saved_columns`);
        if (savedColumns) {
            setLocalColumns(JSON.parse(savedColumns));
        } else {
            setLocalColumns(columns);
        }
    }, [columns]);

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = !allChecked && !noneChecked;
        }
    }, [selectedRowsCountForCurrentPage]);

    const handleResize = React.useCallback((index: number, event: MouseEvent) => {
        const th = resizeRefs.current[index];
        if (!th) {
            return;
        };
        const startX = event.pageX;
        const startWidth = th.offsetWidth;
        const onMouseMove = (e: MouseEvent) => {
            const newWidth = startWidth + (e.pageX - startX);
            th.style.width = `${newWidth}px`;
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    useEffect(() => {
        const listeners: Array<() => void> = [];
        resizeRefs.current.forEach((th, index) => {
            const resizer = th?.querySelector('.resizer');
            if (resizer) {
                const listener = (e: MouseEvent) => handleResize(index, e);
                resizer.addEventListener('mousedown', listener);
                listeners.push(() => resizer.removeEventListener('mousedown', listener));
            }
        });
        return () => {
            listeners.forEach(remove => remove());
        };
    }, [handleResize, localColumns]);

    useEffect(() => {
        setCount(Number(selectedPage) || 1);
    }, [selectedPage]);

    useEffect(() => {
        if (filtersApplied || clearFilters) {
            handleCancelSelection();
            abortBulkDownload();
        }
    }, [filtersApplied, clearFilters]);

    useEffect(() => {
        return () => {
            bulkCancelRef.current = true;
            bulkGenerationRef.current += 1;
        };
    }, []);

    const bulkTargetRows = Math.min(totalCount || 0, BULK_DOWNLOAD_MAX_ROWS);
    const isBulkPreparing = bulkPhase === 'preparing';

    const resetBulkState = () => {
        setBulkPhase('idle');
        setBulkProgress({ fetched: 0, total: 0 });
        setBulkPartial(null);
    };

    function abortBulkDownload() {
        bulkCancelRef.current = true;
        bulkGenerationRef.current += 1;
        resetBulkState();
    }

    const handleOpenBulkConfirm = () => {
        if (!fetchRowsForBulkDownload || bulkTargetRows === 0) {
            return;
        }
        setBulkPhase('confirm');
    };

    const handleStartBulkDownload = async () => {
        if (!fetchRowsForBulkDownload) {
            return;
        }
        const generation = bulkGenerationRef.current;
        bulkCancelRef.current = false;
        setBulkProgress({ fetched: 0, total: bulkTargetRows });
        setBulkPhase('preparing');
        try {
            const result = await collectRowsForBulkDownload(
                totalCount || 0,
                BULK_DOWNLOAD_MAX_ROWS,
                BULK_DOWNLOAD_FETCH_CONCURRENCY,
                fetchRowsForBulkDownload,
                (progress) => {
                    if (generation === bulkGenerationRef.current) {
                        setBulkProgress(progress);
                    }
                },
                () => bulkCancelRef.current || generation !== bulkGenerationRef.current,
            );
            if (result.cancelled || generation !== bulkGenerationRef.current) {
                return;
            }
            if (result.rows.length === 0) {
                setBulkPhase('error');
                return;
            }
            if (result.failedPages.length > 0) {
                setBulkPartial({ rows: result.rows, failedPages: result.failedPages });
                setBulkPhase('partial');
                return;
            }
            resetBulkState();
            downloadToFileAction(result.rows);
        } catch {
            if (generation === bulkGenerationRef.current && !bulkCancelRef.current) {
                setBulkPhase('error');
            }
        }
    };

    const handleBulkPartialContinue = () => {
        const rowsToDownload = bulkPartial ? bulkPartial.rows : [];
        resetBulkState();
        if (rowsToDownload.length > 0) {
            downloadToFileAction(rowsToDownload);
        }
    };

    const handleCancelSelection = () => {
        setSelectedRowData(prev => prev.map(item => ({ ...item, selectedRows: [] })));
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setSelectedRowData(prev => {
            const otherPages = prev.filter(p => p.page !== selectedPage);
            if (isChecked && rows?.length) {
                return [...otherPages, { page: selectedPage, selectedRows: [...rows] }];
            } else {
                return otherPages;
            }
        });
    };

    const handleSingleSelect = (
        event: React.ChangeEvent<HTMLInputElement>,
        row: any
    ) => {
        const isChecked = event.target.checked;

        setSelectedRowData(prev => {
            const otherPages = prev.filter(p => p.page !== selectedPage);
            const currentPageData = prev.find(p => p.page === selectedPage) || {
                page: selectedPage,
                selectedRows: [],
            };

            const updatedSelectedRows = isChecked
                ? [...currentPageData.selectedRows, row]
                : currentPageData.selectedRows.filter(r => r.id !== row.id);

            return [...otherPages, { page: selectedPage, selectedRows: updatedSelectedRows }];
        });
    };

    const handleBlur = (inputValue: number) => {
        if (pageChangeAction) {
            pageChangeAction(inputValue);
        }
    };

    const handlePageSizeChange = (size: number) => {
        setSelectedRowData(prev => prev.map(item => ({ ...item, selectedRows: [] })));
        if (pageSizeChanged) {
            pageSizeChanged(size);
            handleCancelSelection();
        }
    };

    const handleDownloadToFile = () => {
        downloadToFileAction(allSelectedRows);
    };

    const handleSort = (accessor: string) => {
        handleCancelSelection();
        setSort(prev => {
            const isDesc = prev.id === accessor ? !prev.desc : false;
            const updatedSort = { id: accessor, desc: isDesc };
            sortingAction(updatedSort);
            return updatedSort;
        });
    };

    const isRowSelected = (row: any) => {
        return selectedRowIds.has(row.id);
    };

    const toggleColumnVisibility = (index: number) => {
        setLocalColumns((prevColumns) =>
            prevColumns.map((col, i) =>
                i === index ? { ...col, show: !col.show } : col
            )
        );
    };

    const saveConfiguredColumns = () => {
        localStorage.setItem(`${tableId}_saved_columns`, JSON.stringify(localColumns));
        setShowModal(false);
    };

    const resetColumns = () => {
        localStorage.removeItem(`${tableId}_saved_columns`);
        setLocalColumns(columns);
        setShowModal(false);
    };

    return (
        <>
            {showModal &&
                <ModalMain height={330}
                    modal={showModal}
                    title={'Customize columns'}
                    hideMethod={() => setShowModal(false)}
                    secondaryMethod={() => resetColumns()}
                    primaryMethod={() => saveConfiguredColumns()}
                    btnTitle1={'Save'}
                    btnTitle2={'Reset'}>
                    <ul className="drag-list">
                        {localColumns?.map((col, index) => (
                            <li className="drag-list-item" key={col.fieldHeader}>
                                <button type="button" onClick={() => toggleColumnVisibility(index)}>
                                    <i className={col.show ? 'fa fa-eye' : 'fa fa-eye-slash'} aria-hidden="true" />
                                </button>
                                {col.fieldHeader}
                            </li>
                        ))}
                    </ul>
                </ModalMain>
            }
            {bulkPhase !== 'idle' &&
                <div className={bulkPhase === 'preparing' ? 'bulkDownloadModal bulkDownloadModalPreparing' : 'bulkDownloadModal'}>
                <ModalMain height={330}
                    modal={true}
                    title={
                        bulkPhase === 'confirm' ? 'Download all results' :
                        bulkPhase === 'preparing' ? 'Preparing download' :
                        bulkPhase === 'partial' ? 'Some data could not be retrieved' :
                        'Download failed'
                    }
                    hideMethod={
                        bulkPhase === 'confirm' ? () => setBulkPhase('idle') :
                        bulkPhase === 'preparing' ? () => { /* close disabled while selecting */ } :
                        abortBulkDownload
                    }
                    primaryMethod={
                        bulkPhase === 'confirm' ? handleStartBulkDownload :
                        bulkPhase === 'preparing' ? abortBulkDownload : /* hidden via CSS; kept functional as fallback */
                        bulkPhase === 'partial' ? handleBulkPartialContinue :
                        () => setBulkPhase('idle')
                    }
                    secondaryMethod={bulkPhase === 'confirm' ? () => setBulkPhase('idle') : abortBulkDownload}
                    btnTitle1={
                        bulkPhase === 'confirm' ? 'Confirm' :
                        bulkPhase === 'preparing' ? 'Cancel' :
                        bulkPhase === 'partial' ? 'Continue' :
                        'OK'
                    }
                    btnTitle2={'Cancel'}>
                    {bulkPhase === 'confirm' &&
                        <div className="bulk-download-message">
                            {(totalCount || 0) > BULK_DOWNLOAD_MAX_ROWS
                                ? `Do you want to download first ${BULK_DOWNLOAD_MAX_ROWS.toLocaleString()} records out of ${(totalCount || 0).toLocaleString()} records?`
                                : `Do you want to download all ${(totalCount || 0).toLocaleString()} records?`}
                        </div>
                    }
                    {bulkPhase === 'preparing' &&
                        <div className="bulk-download-message">
                            <div className="bulk-download-progress">
                                Selecting {bulkProgress.fetched.toLocaleString()} out of {bulkProgress.total.toLocaleString()} records ...
                            </div>
                        </div>
                    }
                    {bulkPhase === 'partial' && bulkPartial &&
                        <div className="bulk-download-message">
                            {`${bulkPartial.failedPages.length} page(s) of results could not be retrieved. Continue downloading with the ${bulkPartial.rows.length.toLocaleString()} collected records?`}
                        </div>
                    }
                    {bulkPhase === 'error' &&
                        <div className="bulk-download-message">
                            The download could not be prepared. Please try again.
                        </div>
                    }
                </ModalMain>
                </div>
            }
            <div className="custom-table-container">
                {/* TABLE HEADER */}
                <div className="custom-table-header">
                    {/* TABLE TITLE BAR */}
                    {
                        !selectedRowsCountForCurrentPage && (
                            <div className="custom-table-title-bar">
                                {isBulkFeatureEnabled &&
                                    <div className="import-charges">
                                        <span className={'padding-right-medium'}>
                                            <button
                                                type="button"
                                                className="button button-transparent"
                                                onClick={() => onImportChargesClick && onImportChargesClick()}
                                            >
                                                Import charges from file
                                            </button>
                                        </span>
                                    </div>
                                }
                                {isFeatureEnabled && hasUAMPermission && fetchRowsForBulkDownload && allSelectedRows.length === 0 &&
                                    <div className="download-all-results">
                                        <button
                                            type="button"
                                            className="button button-transparent"
                                            disabled={bulkTargetRows === 0 || bulkPhase !== 'idle'}
                                            onClick={handleOpenBulkConfirm}
                                        >
                                            {(totalCount || 0) > BULK_DOWNLOAD_MAX_ROWS
                                                ? `Download first ${BULK_DOWNLOAD_MAX_ROWS.toLocaleString()} of ${(totalCount || 0).toLocaleString()}`
                                                : `Download all ${(totalCount || 0).toLocaleString()} results`}
                                        </button>
                                    </div>
                                }
                                <ul className="pagination">
                                    <li>
                                        <button
                                            type="button"
                                            className="next-page button button-transparent"
                                            onClick={() => setShowModal(true)}
                                        >
                                            <i className="fa fa-cog" aria-hidden="true" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        )
                    }
                    {/* BULK ACTIONS */}
                    {
                        selectedRowsCountForCurrentPage > 0 && (
                            <div className="custom-table-bulk-actions">
                                <div className="actions-left">
                                    <button
                                        type="button"
                                        className="action-button"
                                        onClick={handleDownloadToFile}
                                    >
                                        Download to file
                                    </button>
                                </div>
                                <div className="actions-right">
                                    <div className="selected-count">
                                        <span>{allSelectedRows.length || 0} selected</span>
                                    </div>
                                    <div className="cancel-selection">
                                        <button
                                            type="button"
                                            className="action-button"
                                            onClick={handleCancelSelection}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className="custom-table-scroll">
                    {/* TABLE */}
                    <table className="custom-table">
                        {/* THEAD */}
                        <thead>
                            <tr>
                                {isFeatureEnabled && hasUAMPermission &&
                                    <th className="th-cell header-checkbox">
                                        <input
                                            type="checkbox"
                                            ref={headerCheckboxRef}
                                            checked={allChecked}
                                            disabled={isBulkPreparing}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                }

                                {visibleColumns?.map((col, index) => (
                                    <th
                                        key={col.id}
                                        className={col.sortable ? `th-cell ${col.accessor || ''} sortable` : `th-cell ${col.accessor || ''}`}
                                        title={col.fieldHeader}
                                        ref={el => (resizeRefs.current[index] = el)}
                                    >
                                        <div className="header-content" onClick={() => col.sortable && handleSort(col.accessor)}>
                                            <span className="truncate">{col.fieldHeader}</span>
                                            <i
                                                className={`fa ${sort.id === col.accessor ? (sort.desc === false ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="resizer" />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        {/* TBODY */}
                        <tbody>
                            {formattedRows && formattedRows.length === 0 &&
                                <tr>
                                    <td className="no-rows-found" colSpan={columns?.length}>
                                        <div>
                                            No rows found
                                        </div>
                                    </td>
                                </tr>
                            }
                            {formattedRows?.map((row, rowIndex) => (
                                <tr key={row.id || rowIndex} className={isRowSelected(row) ? 'row-selected' : ''}>
                                    {isFeatureEnabled && hasUAMPermission &&
                                        <td className="td-cell header-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={isRowSelected(row)}
                                                disabled={isBulkPreparing}
                                                onChange={(e) => handleSingleSelect(e, row)}
                                            />
                                        </td>
                                    }
                                    {visibleColumns?.map(col => {
                                        const value = row[col.accessor];
                                        return (
                                            <td key={col.id} className={`td-cell ${col.id || ''}`}>
                                                {col.isActionLink ? (
                                                    <a
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (linkAction) {
                                                                linkAction(row);
                                                            }
                                                        }}
                                                        data-tip={value}>{value}
                                                    </a>
                                                ) : (
                                                    value
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* TABLE FOOTER / PAGINATION */}
                {rows && rows.length > 0 &&
                    <div className="custom-table-footer">
                        <button
                            type="button"
                            className="previous-page-btn pagination-btn"
                            disabled={count === 1}
                            onClick={() => previousPageAction && previousPageAction()}
                        >
                            <i className="fa fa-chevron-left" /> Previous
                        </button>
                        <div className="change-page-number">
                            <span>Page</span>
                            <input
                                type="number"
                                value={count}
                                className="page-number-input"
                                min="1"
                                onChange={(e) => setCount(Number(e.target.value))}
                                onBlur={(e) => handleBlur(Number(e.target.value))}
                            />
                            <span>of {pageCount}</span>
                        </div>
                        <div className="change-page-size">
                            <select
                                className="page-size-select"
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            >
                                <option value="5">5 rows</option>
                                <option value="10">10 rows</option>
                                <option value="20">20 rows</option>
                                <option value="25">25 rows</option>
                                <option value="50">50 rows</option>
                                <option value="100">100 rows</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            className="next-page-btn pagination-btn"
                            disabled={count === pageCount}
                            onClick={() => nextPageAction && nextPageAction()}
                        >
                            Next <i className="fa fa-chevron-right" />
                        </button>
                    </div>
                }
            </div>
        </>
    );
};

export default CustomTable;